import { Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map, startWith } from 'rxjs';
import { PageHeaderComponent } from '../../components/page-header/page-header.component';

/**
 * Shared "coming soon" surface for navigation destinations whose feature page
 * isn't built yet. It keeps the redesigned information architecture fully
 * navigable — every sidebar item leads somewhere honest — without standing up
 * a dozen real feature pages. The section title is derived from the URL so one
 * component serves every placeholder route.
 */
const SECTION_LABELS: Record<string, string> = {
  overview: 'Overview',
  divisions: 'Divisions',
  participants: 'Participants',
  programs: 'Programs',
  rosters: 'Rosters / Seats',
  billing: 'Billing',
  members: 'Team & access',
  settings: 'Settings',
  'audit-log': 'Audit Log',
  'feature-flags': 'Feature Flags',
  roles: 'Roles & Permissions',
  revenue: 'Platform Revenue',
  impersonation: 'Impersonation',
  'api-docs': 'API Docs',
};

const ID_LIKE = /[0-9]/; // org/division ids contain digits — a bare id = overview

@Component({
  selector: 'app-placeholder',
  standalone: true,
  imports: [PageHeaderComponent],
  template: `
    <div class="ed-page">
      <app-page-header eyebrow="Coming soon">
        <h1 class="ph-headline">{{ title() }}</h1>
        <p class="ph-lede">
          This section is part of the redesigned admin navigation. The page itself
          is on the build list — the structure and access rules are wired, the
          content lands here next.
        </p>
      </app-page-header>
    </div>
  `,
})
export class PlaceholderComponent {
  private router = inject(Router);

  private url = toSignal(
    this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      map(() => this.router.url),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );

  readonly title = computed(() => {
    const segs = this.url().split('?')[0].split('#')[0].split('/').filter(Boolean);
    const last = segs[segs.length - 1] ?? '';
    if (SECTION_LABELS[last]) return SECTION_LABELS[last];
    // A trailing id segment (e.g. /organizations/abc123) is an entity overview.
    if (ID_LIKE.test(last) || segs[segs.length - 2] === 'divisions') return 'Overview';
    return last
      .split('-')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  });
}
